/* Drivers and parents: invite, add a driver, manage an account, fix
   children without a parent, answer Families link requests (2026-10-28).
   All of it used to open the classic dashboard's Accounts tab. */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { createDriver, CreatedDriver } from '../services/driversApi';
import {
  bulkInviteParents, BulkInviteResult, FamilySchoolLink, listSchoolLinks, respondToSchoolLink,
} from '../services/paymentsApi';
import { ConsoleModal, fieldInput, fieldSelect, ModalButtons, ModalField } from './Modal';
import { ago, busWithPlate, Chip, initials, plural, useAction, useToast } from './kit';
import type { FleetModel, Person } from './data/model';
import {
  childrenOf, EMAIL_RE, InviteRole, isSuspended, moveChildren, PendingInvite, removeAccount, removeRosterRow,
  resendInvite, sendInvites, setTransportHold, suspendDriver, unlinkedChildren, usePendingInvites,
} from './data/peopleActions';

const segStyle = (on: boolean): React.CSSProperties => ({
  flex: 1, height: 40, border: 0, borderRadius: 10, cursor: 'pointer', font: 'inherit', fontSize: 15, fontWeight: 600,
  background: on ? 'var(--s1)' : 'transparent', color: 'var(--ink)', boxShadow: on ? '0 1px 2px rgba(17,21,32,.12)' : 'none',
});

/* ── Invite ─────────────────────────────────────────────────────────────── */
export const InviteModal: React.FC<{ f: FleetModel; initialRole: InviteRole; onClose: () => void }> = ({ f, initialRole, onClose }) => {
  const { currentUser } = useAuth();
  const { toast } = useToast();
  const action = useAction();
  const pending = usePendingInvites(f.org.code);
  const [role, setRole] = useState<InviteRole>(initialRole);
  const [chips, setChips] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState<string | null>(null);
  const feesOn = (f.org.raw.addons as Record<string, { status?: string }> | undefined)?.fees?.status === 'connected';

  const add = (raw: string) => {
    const addr = raw.trim().replace(/[,;]+$/, '').toLowerCase();
    if (!addr) return true;
    if (!EMAIL_RE.test(addr)) { setErr(`${addr} is not an email address.`); return false; }
    if (pending.some((p) => p.role === role && p.email === addr)) { setErr(`${addr} is already invited — use Send again below.`); setDraft(''); return false; }
    setChips((c) => (c.includes(addr) ? c : [...c, addr]));
    setDraft('');
    setErr(null);
    return true;
  };

  const send = () => {
    if (busy || !currentUser) return;
    // The same spelling add() keeps: trailing commas off, lower case.
    const typed = draft.trim().replace(/[,;]+$/, '').toLowerCase();
    const list = typed ? (add(draft) ? [...chips, typed].filter((v, i, a) => a.indexOf(v) === i) : null) : chips;
    if (!list || list.length === 0) { if (!err) setErr('Type an email address, then press Enter.'); return; }
    setBusy(true);
    sendInvites(f, currentUser.uid, role, list, pending)
      .then((o) => {
        setChips(o.failed);
        if (o.rateLimited) toast({ kind: 'error', text: 'Too many invites at once. Wait a few minutes, then send the rest.' });
        else if (o.failed.length && o.sent) toast({ kind: 'warning', text: `${plural(o.sent, 'invite')} sent. ${plural(o.failed.length, 'address', 'addresses')} could not be sent and stay in the box.` });
        else if (o.failed.length) toast({ kind: 'error', text: o.lastError || 'The invites did not go. Try again in a minute.' });
        else if (o.sent) toast({ kind: 'success', text: `${plural(o.sent, 'invite')} sent` });
        if (o.skipped) toast({ kind: 'info', text: `${plural(o.skipped, 'address was', 'addresses were')} already invited — use Send again.` });
      })
      .catch(() => toast({ kind: 'error', text: 'The invites did not go. Try again in a minute.' }))
      .finally(() => setBusy(false));
  };

  const again = (inv: PendingInvite) => {
    if (!currentUser || resending) return;
    setResending(inv.id);
    void action({ busy: `Sending to ${inv.email} again…`, done: `Invite sent again to ${inv.email}`, fail: `Couldn’t send to ${inv.email}.`, run: () => resendInvite(f, currentUser.uid, inv) })
      .finally(() => setResending(null));
  };

  const waiting = pending.filter((p) => p.role === role);
  return (
    <ConsoleModal title={role === 'bus_driver' ? 'Invite drivers' : 'Invite parents'} onClose={onClose} width={600}
      footer={<ModalButtons onCancel={onClose} primary={chips.length > 1 ? `Send ${chips.length} invites` : 'Send invite'} onPrimary={send} busy={busy} />}>
      <div style={{ display: 'flex', gap: 4, padding: 4, borderRadius: 12, background: 'var(--s2)' }} role="tablist">
        <button type="button" role="tab" aria-selected={role === 'bus_driver'} style={segStyle(role === 'bus_driver')} onClick={() => { setRole('bus_driver'); setErr(null); }}>Drivers</button>
        <button type="button" role="tab" aria-selected={role === 'parent'} style={segStyle(role === 'parent')} onClick={() => { setRole('parent'); setErr(null); }}>Parents</button>
      </div>
      <p style={{ margin: 0, color: 'var(--muted)', fontSize: 15 }}>
        {role === 'bus_driver'
          ? 'Each driver gets an email with a link to join your school in the Trasse app.'
          : 'Each parent gets an email with a link to join and add their child.'}
      </p>
      <ModalField label="Email addresses" error={err} hint="Press Enter after each one. Paste a list with commas.">
        {(id, ring) => (
          <span style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', flex: 1, padding: chips.length ? '8px 0' : 0 }}>
            {chips.map((c) => (
              <span key={c} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 30, padding: '0 6px 0 12px', borderRadius: 999, background: 'var(--s1)', fontSize: 15 }}>
                {c}
                <button type="button" aria-label={`Remove ${c}`} onClick={() => setChips((x) => x.filter((y) => y !== c))}
                  style={{ width: 22, height: 22, border: 0, borderRadius: '50%', background: 'var(--s2)', color: 'var(--muted)', cursor: 'pointer' }}>×</button>
              </span>
            ))}
            <input id={id} {...ring} style={{ ...fieldInput, minWidth: 200 }} value={draft} type="email" placeholder={chips.length ? '' : 'name@example.com'}
              onChange={(e) => { setDraft(e.target.value); setErr(null); }}
              onPaste={(e) => {
                const t = e.clipboardData.getData('text');
                if (/[,;\s]/.test(t.trim())) { e.preventDefault(); t.split(/[,;\s]+/).forEach((x) => { if (x) add(x); }); }
              }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(draft); } else if (e.key === 'Backspace' && !draft && chips.length) setChips((x) => x.slice(0, -1)); }} />
          </span>
        )}
      </ModalField>
      {role === 'parent' && feesOn && <BulkParents orgCode={f.org.code} />}
      {waiting.length > 0 && (
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Waiting to join · {waiting.length}</div>
          <div style={{ borderRadius: 14, background: 'var(--s2)', padding: '2px 14px', maxHeight: 220, overflow: 'auto' }}>
            {waiting.map((inv, i) => (
              <div key={inv.id} className="row" style={{ gap: 10, padding: '10px 0', borderTop: i ? '1px solid var(--line)' : undefined }}>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{inv.email}</span>
                <span className="sm" style={{ color: 'var(--muted)' }}>{inv.createdAt ? ago(inv.createdAt) : ''}</span>
                <button type="button" className="btn s b-line" disabled={!!resending} onClick={() => again(inv)}>{resending === inv.id ? 'Sending…' : 'Send again'}</button>
              </div>
            ))}
          </div>
          <p className="sm" style={{ margin: '6px 0 0', color: 'var(--muted)' }}>An invite lasts a day. After that, invite them again.</p>
        </div>
      )}
    </ConsoleModal>
  );
};

/** ParentsBulkInvite, in the console: count first (dry run), then send. */
const BulkParents: React.FC<{ orgCode: string }> = ({ orgCode }) => {
  const { toast } = useToast();
  const [scope, setScope] = useState<'paid' | 'all'>('paid');
  const [count, setCount] = useState<BulkInviteResult | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let live = true;
    setCount(null);
    bulkInviteParents(orgCode, scope, true).then((r) => { if (live) setCount(r); });
    return () => { live = false; };
  }, [orgCode, scope]);
  const n = count?.ok ? count.counts?.toInvite ?? 0 : 0;
  const go = () => {
    if (busy || !n) return;
    setBusy(true);
    bulkInviteParents(orgCode, scope, false)
      .then((r) => {
        if (!r.ok) { toast({ kind: 'error', text: r.message || 'The invites did not go.' }); return; }
        toast({ kind: (r.failed || 0) > 0 ? 'warning' : 'success', text: `${plural(r.sent || 0, 'parent')} invited${r.failed ? `, ${r.failed} failed` : ''}` });
        setCount(null);
        return bulkInviteParents(orgCode, scope, true).then(setCount);
      })
      .finally(() => setBusy(false));
  };
  return (
    <div style={{ borderRadius: 14, background: 'var(--s2)', padding: 14, display: 'grid', gap: 10 }}>
      <b style={{ fontSize: 15 }}>Everyone on your fees list</b>
      <div className="row" style={{ gap: 14, flexWrap: 'wrap', fontSize: 15 }}>
        <label className="row" style={{ gap: 6 }}><input type="radio" checked={scope === 'paid'} onChange={() => setScope('paid')} />Paid up</label>
        <label className="row" style={{ gap: 6 }}><input type="radio" checked={scope === 'all'} onChange={() => setScope('all')} />Everyone</label>
      </div>
      <div className="row" style={{ gap: 10, justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <span className="sm" style={{ color: 'var(--muted)' }}>
          {!count ? 'Counting…' : !count.ok ? (count.message || 'Could not count the list.') : n ? `${plural(n, 'parent')} to invite by email.` : 'Everyone on the list is invited already.'}
        </span>
        <button type="button" className="btn s b-ink" disabled={!n || busy} onClick={go}>{busy ? 'Inviting…' : n ? `Invite ${n}` : 'Invite'}</button>
      </div>
    </div>
  );
};

/* ── Add a driver yourself (CreateDriverButton) ─────────────────────────── */
export const AddDriverModal: React.FC<{ f: FleetModel; onClose: () => void }> = ({ f, onClose }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [busId, setBusId] = useState('');
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<CreatedDriver | null>(null);
  const { toast } = useToast();
  const nameErr = touched && name.trim().length < 2 ? 'Type their name.' : null;
  const emailErr = touched && !EMAIL_RE.test(email.trim()) ? 'Type the email they will sign in with.' : null;

  const submit = () => {
    setTouched(true);
    if (name.trim().length < 2 || !EMAIL_RE.test(email.trim()) || busy) return;
    setBusy(true); setErr(null);
    createDriver({ orgCode: f.org.code, email: email.trim().toLowerCase(), displayName: name.trim(), busId: busId || undefined })
      .then(setDone)
      .catch((e: Error) => setErr(e.message || 'Could not create the driver.'))
      .finally(() => setBusy(false));
  };
  const copy = (t: string) => { void navigator.clipboard?.writeText(t).then(() => toast({ kind: 'success', text: 'Copied' })); };

  if (done) {
    return (
      <ConsoleModal title={done.created ? 'Driver added' : 'Driver linked'} onClose={onClose}
        footer={<div style={{ display: 'flex', justifyContent: 'flex-end' }}><button type="button" className="btn b-ink" onClick={onClose}>Done</button></div>}>
        <p style={{ margin: 0, fontSize: 16, lineHeight: '23px' }}>
          {done.created ? 'Hand these to the driver in person. The password is shown only now.' : 'This email already had a Trasse account. It is now a driver at your school and signs in with its own password.'}
        </p>
        <div style={{ borderRadius: 14, background: 'var(--s2)', padding: '4px 16px' }}>
          {[['Email', done.email], ...(done.password ? [['Password', done.password]] : [])].map(([k, v], i) => (
            <div key={k} className="row" style={{ gap: 10, padding: '12px 0', borderTop: i ? '1px solid var(--line)' : undefined }}>
              <span style={{ width: 90, color: 'var(--muted)', fontSize: 14 }}>{k}</span>
              <b className="mono" style={{ flex: 1, fontSize: 16 }}>{v}</b>
              <button type="button" className="btn s b-line" onClick={() => copy(v)}>Copy</button>
            </div>
          ))}
        </div>
        {done.busLinked === false && (
          <p style={{ margin: 0, color: 'var(--warn)', fontWeight: 600, fontSize: 15 }}>
            The account is ready, but it could not be put on the bus. Assign the bus from Buses → Add bus or ask Trasse.
          </p>
        )}
      </ConsoleModal>
    );
  }
  return (
    <ConsoleModal title="Add a driver" onClose={onClose} onSubmit={submit} enterHint="add"
      footer={<ModalButtons onCancel={onClose} primary="Add driver" onPrimary={submit} busy={busy} />}>
      <p style={{ margin: 0, color: 'var(--muted)', fontSize: 15 }}>For a driver without email at hand: Trasse makes the account now and gives you a password to hand over.</p>
      <ModalField label="Name" error={nameErr}>{(id, ring) => <input id={id} {...ring} style={fieldInput} value={name} placeholder="Joseph Kamau" onChange={(e) => setName(e.target.value)} />}</ModalField>
      <ModalField label="Email (their login)" error={emailErr}>{(id, ring) => <input id={id} {...ring} style={fieldInput} type="email" value={email} placeholder="joseph@example.com" onChange={(e) => setEmail(e.target.value)} />}</ModalField>
      <ModalField label="Bus">
        {(id, ring) => (
          <select id={id} {...ring} style={{ ...fieldSelect, color: busId ? 'var(--ink)' : 'var(--muted)' }} value={busId} onChange={(e) => setBusId(e.target.value)}>
            <option value="">Choose later</option>
            {f.buses.map((b) => <option key={b.id} value={b.id}>{busWithPlate(b)}</option>)}
          </select>
        )}
      </ModalField>
      {err && <span role="alert" style={{ color: 'var(--bad)', fontWeight: 600 }}>{err}</span>}
    </ConsoleModal>
  );
};

/* ── One account: the side sheet ────────────────────────────────────────── */
export const PersonSheet: React.FC<{ f: FleetModel; person: Person; top: number; onClose: () => void }> = ({ f, person: p, top, onClose }) => {
  const { currentUser } = useAuth();
  const action = useAction();
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [moveTo, setMoveTo] = useState<Record<string, string>>({});
  const closeBtn = useRef<HTMLButtonElement>(null);
  useEffect(() => { closeBtn.current?.focus(); }, [p.id]);
  const isDriver = p.role === 'driver';
  const kids = useMemo(() => (isDriver ? [] : childrenOf(f, p)), [f, p, isDriver]);
  const suspended = isSuspended(p);
  const run = (busyText: string, done: string, fail: string, fn: () => Promise<unknown>) => {
    if (busy) return;
    setBusy(true);
    void action({ busy: busyText, done, fail, run: fn }).finally(() => setBusy(false));
  };

  return (
    <aside className="sheet" aria-label={p.name} style={{ top }}>
      <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--line)', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <span className="av" style={{ width: 44, height: 44, fontSize: 15 }}>{initials(p.name)}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <span className="mono">{isDriver ? 'Driver' : 'Parent'}{p.lastSeen ? ` · seen ${ago(p.lastSeen)}` : ''}</span>
          <h2 className="h2" style={{ fontSize: 24, marginTop: 4 }}>{p.name}</h2>
          {suspended && <div style={{ marginTop: 6 }}><Chip tone="warn">Suspended</Chip></div>}
        </div>
        <button ref={closeBtn} type="button" className="ibtn" aria-label="Close" onClick={onClose}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
        </button>
      </div>
      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 20, overflow: 'auto', flex: 1 }}>
        <div className="kv">
          <div><span>Phone</span><b className="num">{p.phone || '—'}</b></div>
          <div><span>Email</span><b style={{ overflowWrap: 'anywhere' }}>{p.email || '—'}</b></div>
          {isDriver && <div><span>Bus</span><b>{p.busName || 'No bus'}</b></div>}
        </div>
        {!isDriver && (
          <div>
            <h3 className="h3" style={{ marginBottom: 8 }}>Children</h3>
            {kids.length === 0 && <p className="sm" style={{ margin: 0, color: 'var(--muted)' }}>No child linked yet.</p>}
            {kids.map((k, i) => (
              <div key={k.childId || k.name} style={{ padding: '12px 0', borderTop: i ? '1px solid var(--line)' : undefined, display: 'grid', gap: 8 }}>
                <div className="row" style={{ justifyContent: 'space-between', gap: 10 }}>
                  <b>{k.name}</b>
                  <span className="sm" style={{ color: k.onRoster ? 'var(--muted)' : 'var(--warn)' }}>{k.onRoster ? `${k.busName}${k.held ? ' · transport on hold' : ''}` : 'On no bus list'}</span>
                </div>
                {k.onRoster && <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                  <select className="tin" style={{ height: 32, flex: 1, minWidth: 140 }} aria-label={`Move ${k.name} to another bus`} value={moveTo[k.childId] || ''}
                    onChange={(e) => setMoveTo((m) => ({ ...m, [k.childId]: e.target.value }))}>
                    <option value="">Move to another bus…</option>
                    {f.buses.filter((b) => b.id !== k.busId).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                  <button type="button" className="btn s b-line" disabled={busy || !moveTo[k.childId]}
                    onClick={() => run(`Moving ${k.name}…`, `${k.name} moved`, `Couldn’t move ${k.name}.`, () => moveChildren(f, p, [k.childId], moveTo[k.childId]))}>Move</button>
                  <button type="button" className="btn s b-line" disabled={busy}
                    onClick={() => run(k.held ? 'Resuming…' : 'Holding…', k.held ? `${k.name} rides again` : `${k.name}’s transport is on hold`, 'Couldn’t save that.', () => setTransportHold(f, [k.childId], !k.held))}>
                    {k.held ? 'Resume transport' : 'Hold transport'}
                  </button>
                </div>}
              </div>
            ))}
            {kids.some((k) => k.onRoster) && <p className="sm" style={{ margin: '4px 0 0', color: 'var(--muted)' }}>A hold keeps the parent’s login but takes the child off the driver’s list.</p>}
            {kids.some((k) => !k.onRoster) && <p className="sm" style={{ margin: '4px 0 0', color: 'var(--muted)' }}>A child on no bus list is added from the parent’s app: they pick the bus when they add the child.</p>}
          </div>
        )}
      </div>
      <div style={{ padding: '14px 20px', borderTop: '1px solid var(--line)', display: 'flex', gap: 8 }}>
        {isDriver && (
          <button type="button" className="btn b-line" style={{ flex: 1 }} disabled={busy}
            onClick={() => run(suspended ? 'Restoring…' : 'Suspending…', suspended ? `${p.name} can sign in again` : `${p.name} is suspended`, 'Couldn’t save that.', () => suspendDriver(p, !suspended))}>
            {suspended ? 'Unsuspend' : 'Suspend'}
          </button>
        )}
        <button type="button" className="btn b-line" style={{ flex: 1, color: 'var(--bad)' }} disabled={busy || p.id === currentUser?.uid} onClick={() => setConfirmRemove(true)}>Remove from school</button>
      </div>
      {confirmRemove && (
        <ConsoleModal title={`Remove ${p.name}?`} onClose={() => setConfirmRemove(false)} width={520}
          footer={<ModalButtons cancel="Keep" onCancel={() => setConfirmRemove(false)} primary="Remove" danger busy={busy}
            onPrimary={() => run(`Removing ${p.name}…`, `${p.name} removed`, `Couldn’t remove ${p.name}.`, async () => { await removeAccount(f, p, currentUser?.uid || ''); setConfirmRemove(false); onClose(); })} />}>
          <p style={{ margin: 0, fontSize: 17, lineHeight: '25px' }}>
            They are signed out and lose access to your school.{' '}
            {isDriver ? 'Their bus stays, without them.' : kids.length ? `${plural(kids.length, 'child comes', 'children come')} off the bus lists.` : ''}
          </p>
        </ConsoleModal>
      )}
    </aside>
  );
};

/* ── Children with no current parent ────────────────────────────────────── */
export const UnlinkedModal: React.FC<{ f: FleetModel; onClose: () => void }> = ({ f, onClose }) => {
  const action = useAction();
  const rows = unlinkedChildren(f);
  const [busy, setBusy] = useState<string | null>(null);
  return (
    <ConsoleModal title="Children with no parent" onClose={onClose} width={600}
      footer={<div style={{ display: 'flex', justifyContent: 'flex-end' }}><button type="button" className="btn b-ink" onClick={onClose}>Done</button></div>}>
      <p style={{ margin: 0, color: 'var(--muted)', fontSize: 15 }}>
        Their parent’s account was removed or never linked, so nobody gets their pickup alerts. Ask the parent to join with your parent code and add the child — or take the row off the bus if the child has left.
      </p>
      {rows.length === 0 && <p style={{ margin: 0 }}>Every child on a bus has a parent now.</p>}
      <div style={{ borderRadius: 14, background: 'var(--s2)', padding: '2px 14px', maxHeight: 320, overflow: 'auto' }}>
        {rows.map((r, i) => (
          <div key={`${r.busId}:${r.childId || r.name}`} className="row" style={{ gap: 10, padding: '10px 0', borderTop: i ? '1px solid var(--line)' : undefined }}>
            <b style={{ flex: 1 }}>{r.name}</b>
            <span className="sm" style={{ color: 'var(--muted)' }}>{r.busName}</span>
            <button type="button" className="btn s b-line" disabled={!!busy}
              onClick={() => { setBusy(r.childId || r.name); void action({ busy: `Taking ${r.name} off ${r.busName}…`, done: `${r.name} is off ${r.busName}`, fail: 'Couldn’t change the bus list.', run: () => removeRosterRow(r.busId, r.childId, r.name) }).finally(() => setBusy(null)); }}>
              Take off the bus
            </button>
          </div>
        ))}
      </div>
    </ConsoleModal>
  );
};

/* ── Families asking to link (SchoolFamilyLinksPanel) ───────────────────── */
export const FamilyLinkRequests: React.FC<{ orgCode: string }> = ({ orgCode }) => {
  const { toast } = useToast();
  const [links, setLinks] = useState<FamilySchoolLink[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const load = React.useCallback(() => { if (orgCode) listSchoolLinks(orgCode).then((r) => setLinks(r.links)); }, [orgCode]);
  useEffect(load, [load]);
  const asking = links.filter((l) => l.status === 'requested');
  if (asking.length === 0) return null;
  const answer = (l: FamilySchoolLink, a: 'accept' | 'decline') => {
    setBusy(l.linkId);
    respondToSchoolLink(l.linkId, a)
      .then((r) => {
        if (!r.ok) { toast({ kind: 'error', text: r.message || 'That did not go through.' }); return; }
        toast({ kind: 'success', text: a === 'accept' ? `${l.familyName} is linked — you can add their children to trips` : `${l.familyName} declined` });
        load();
      })
      .finally(() => setBusy(null));
  };
  return (
    <section className="panel">
      <div className="ph">
        <div>
          <h2 className="h2">Families asking to link</h2>
          <p className="sm" style={{ margin: '2px 0 0', color: 'var(--muted)' }}>Trasse Families households whose children are at your school. Linking lets you add them to school trips; you never see their car.</p>
        </div>
      </div>
      {asking.map((l) => (
        <div key={l.linkId} className="row" style={{ padding: '12px 20px', gap: 12, borderTop: '1px solid var(--line)', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <b>{l.familyName}</b>
            <div className="sm" style={{ color: 'var(--muted)' }}>{(l.children || []).map((c) => c.childName).join(', ') || plural(l.childIds.length, 'child', 'children')}</div>
          </div>
          <button type="button" className="btn s b-line" disabled={busy === l.linkId} onClick={() => answer(l, 'decline')}>Decline</button>
          <button type="button" className="btn s b-ink" disabled={busy === l.linkId} onClick={() => answer(l, 'accept')}>Accept</button>
        </div>
      ))}
    </section>
  );
};
